































































































import Vue from "vue";
import dayjs from "dayjs";
import eventBus from "@/services/eventBus";
import api from "../services/api";
import { DimensionRebookingBookResult, DimensionRebookingRow } from "../models/DimensionRebooking";

// K2D "Javítás -> Átkönyvelés excelből" (m_bok_dim_xls) + "Átkönyvelési sablon"
// (m_bok_dim_xls_sablon) portja. A fájl betöltése csak beolvas/validál; az Átvezetés
// soronként hívja az spp_bok_to_dim tárolt eljárást (all-or-nothing). A K2D a hibákat
// az Excelbe írta vissza — itt (a NovitaxImportView mintájára) egy összegző piros
// listában jelennek meg a grid alatt, a sorszámmal hivatkozva; a javítás a fájlban
// történik, majd újratöltés (a sorok nem szerkeszthetők, mint a K2D-ben sem).

interface Data {
  file: File | null;
  rows: DimensionRebookingRow[];
  bookResult: DimensionRebookingBookResult | null;
  // A betöltés/átvezetés összegző üzenete — a hiba-alert fejléce (NovitaxImportView minta).
  resultMessage: string;
  columns: object[];
}

interface Methods {
  uploadFile(): Promise<void>;
  book(): Promise<void>;
  clearRows(): void;
  downloadTemplate(): Promise<void>;
  formatDate(d: string | null): string;
  formatTotal(t: number): string;
}

interface Computed {
  canBook: boolean;
  errorRows: DimensionRebookingRow[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  name: "DimensionRebookingView",

  data(): Data {
    return {
      file: null,
      rows: [],
      bookResult: null,
      resultMessage: "",
      columns: [
        { field: "rowNumber", title: "Sor", width: "70px" },
        { field: "accno", title: "Főkönyvi szám", width: "130px" },
        { field: "fromDimCode", title: "Induló költséghely", width: "150px" },
        { field: "toDimCode", title: "Cél költséghely", width: "150px" },
        { field: "amount", title: "Összeg", width: "120px", cell: "amountCell" },
        { field: "accDate", title: "Könyvelés dátuma", width: "140px", cell: "accDateCell" },
        { field: "remark", title: "Megjegyzés" },
        { field: "bookTypeCode", title: "Napló", width: "80px" },
      ],
    };
  },

  computed: {
    canBook(): boolean {
      return this.rows.length > 0 && this.rows.every((r) => !r.error);
    },

    // A hibás sorok — a Novitax-stílusú piros lista forrása (sorszám + üzenet).
    errorRows(): DimensionRebookingRow[] {
      return this.rows.filter((r) => !!r.error);
    },
  },

  methods: {
    formatDate(d: string | null): string {
      return d ? dayjs(d).format("YYYY.MM.DD.") : "";
    },

    formatTotal(t: number): string {
      return (t ?? 0).toLocaleString("hu-HU");
    },

    async uploadFile() {
      if (!this.file) return;
      this.bookResult = null;
      eventBus.$emit("busy-message", true);
      try {
        const result = await api.UploadDimensionRebookingFile(this.file);
        this.rows = result.rows || [];
        this.resultMessage = result.message;
        if (result.success) {
          eventBus.$emit("success", result.message);
        } else {
          eventBus.$emit("error", result.message);
        }
      } catch (e) {
        console.error(e);
        this.rows = [];
        this.resultMessage = "";
        eventBus.$emit("error", "A fájl betöltése sikertelen!");
      } finally {
        eventBus.$emit("busy-message", false);
      }
    },

    async book() {
      if (!this.canBook) return;
      this.bookResult = null;
      eventBus.$emit("busy-message", true);
      try {
        const result = await api.BookDimensionRebooking(this.rows);
        this.bookResult = result;
        // A backend a sorokat a hibaüzenetekkel / tranzakció-id-kkal adja vissza.
        this.rows = result.rows || this.rows;
        this.resultMessage = result.message;
        if (result.success) {
          eventBus.$emit("success", result.message);
          this.file = null;
        } else {
          eventBus.$emit("error", result.message);
        }
      } catch (e) {
        console.error(e);
        eventBus.$emit("error", "Az átvezetés sikertelen!");
      } finally {
        eventBus.$emit("busy-message", false);
      }
    },

    clearRows() {
      this.rows = [];
      this.bookResult = null;
      this.resultMessage = "";
      this.file = null;
    },

    async downloadTemplate() {
      try {
        const base64 = await api.GetDimensionRebookingTemplate();
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats;base64,${base64}`;
        link.download = "atkonyvelesi_sablon.xlsx";
        link.click();
      } catch (e) {
        console.error(e);
        eventBus.$emit("error", "A sablon letöltése sikertelen!");
      }
    },
  },
});
